<template>
	<div
		class="flex h-full max-w-(--breakpoint-2xl) flex-col overflow-y-auto pb-12"
	>
		<div class="pt-6 text-center">
			<HeadingLarge
				v-if="headingLargeHtml"
				:richTextHtml="headingLargeHtml"
				class="pb-2"
			/>

			<HeadingSmall
				v-if="blok?.headingSmall"
				:headingText="blok.headingSmall"
				no-underline
			/>
		</div>
		<button
			type="button"
			@click="$emit('close-with-slide')"
			class="bg-accent-400 text-accent-700 hover:bg-accent-700 absolute right-8 bottom-8 z-10 flex flex-row items-center gap-2 rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white"
		>
			{{ t('essentialsSlide.allEssentials') }}
			<ArrowLongRightIcon class="size-6" />
		</button>
		<div class="flex flex-1 justify-center gap-6">
			<section
				v-for="{ bundle, plan, basePackage } in essentialsBundles"
				:key="bundle.id + '-' + plan.id"
				class="ring-secondary-100 relative flex w-1/2 flex-col rounded-2xl border border-slate-200 bg-white shadow-xs ring-1"
			>
				<!-- Green top accent bar-->
				<div class="bg-accent-300 h-1.5 rounded-t-full"></div>

				<!-- Card Header Content -->
				<div class="px-6 pt-6">
					<div class="flex items-center gap-2">
						<img
							:src="HeartShieldIcon"
							alt=""
						/>
						<h3 class="text-secondary-900 text-xl font-bold">
							{{ t('essentialsSlide.includedFor') }} {{ plan.firstName }}
						</h3>
					</div>
					<p
						v-if="basePackage"
						class="text-md text-secondary-600 mt-2"
					>
						{{ t('essentialsSlide.itemsInBundle') }} {{ basePackage.name }}
					</p>
				</div>

				<!-- Card Package Sub Items -->
				<ul class="mt-6 flex flex-col gap-3">
					<li
						v-for="productInPackage in getPackageSubItems(
							bundle.id,
							false,
							bundleIncludesEverplans(basePackage, bundle)
						)"
						:key="productInPackage.lineItem?.id"
						class="mx-4 rounded-md px-4 py-3"
						:class="
							productInPackage.isIncludedInPackage !== false
								? 'bg-background-50'
								: 'bg-slate-50'
						"
					>
						<div
							v-if="productInPackage.lineItem"
							class="flex flex-col gap-2"
						>
							<!-- Main line item row -->
							<div class="flex items-center justify-between gap-2">
								<div class="flex items-center gap-2">
									<component
										:is="
											productInPackage.isIncludedInPackage !== false
												? productInPackage.lineItem?.isGuaranteed
													? ShieldCheckIcon
													: CheckCircleIcon
												: XCircleIcon
										"
										class="size-7 shrink-0"
										:class="
											productInPackage.isIncludedInPackage !== false
												? 'text-accent-600'
												: 'text-red-500'
										"
									/>

									<span class="text-md text-secondary-900 font-medium">
										{{ productInPackage.lineItem?.name }}
									</span>
								</div>

								<!-- Info Icon (only for Everplans item) -->
								<button
									v-if="
										productInPackage.lineItem?.id ===
										'EverplansLifetimeLineItem'
									"
									type="button"
									@click="everplansModalOpen = true"
									class="text-primary-500 hover:text-primary-700"
								>
									<InformationCircleIcon class="text-primary-400 size-6" />
								</button>
							</div>

							<!-- People responsible (only for Everplans item) -->
							<div
								v-if="
									productInPackage.lineItem?.id ===
										'EverplansLifetimeLineItem' &&
									plan.peopleResponsible?.some((p) => p.firstName || p.lastName)
								"
								class="text-md text-primary-500 ml-9 truncate italic"
							>
								{{ formatPeople(plan) }}
							</div>
						</div>
					</li>
				</ul>

				<!-- Package selection per plan -->
				<div class="mt-auto px-6 pb-6">
					<InputQuantity
						class="my-4 w-full"
						:plan="plan"
						:product="bundle"
						:include-everplans="bundleIncludesEverplans(basePackage, bundle)"
					/>
				</div>
			</section>
		</div>
		<EverplansItemModal v-model="everplansModalOpen" />
	</div>
</template>

<script setup lang="ts">
import HeadingLarge from '../presentation/HeadingLarge.vue'
import HeadingSmall from '../presentation/HeadingSmall.vue'
import type { PbLineItem, PbPackageProduct } from '@/apiTypes'
import HeartShieldIcon from '@/assets/heart-shield-icon.svg'
import type Plan from '@/classes/Plan'
import EverplansItemModal from '@/components/merchandise/EverplansItemModal.vue'
import InputQuantity from '@/components/merchandise/InputQuantity.vue'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { EssentialSlideStoryblok } from '@/sbTypes'
import { useSessionStore } from '@/stores/session'
import XCircleIcon from '@heroicons/vue/24/outline/XCircleIcon'
import {
	ArrowLongRightIcon,
	CheckCircleIcon,
	ShieldCheckIcon,
	InformationCircleIcon,
} from '@heroicons/vue/24/solid'
import { computed, ref } from 'vue'

const everplansModalOpen = ref(false)

defineProps<{
	blok?: EssentialSlideStoryblok
	headingLargeHtml?: string
	headingSmallHtml?: string
}>()

const { priceBook } = useMerchandise()
const session = useSessionStore()

const { t } = usePresentationLocale()

defineEmits<{
	(event: 'close-with-slide'): void
}>()

/**
 * Format a list of people responsible on the plan.
 */
function formatPeople(plan: Plan) {
	return (plan.peopleResponsible ?? [])
		.map((p) => `${p.firstName} ${p.lastName ?? ''}`)
		.join(', ')
}

/**
 * For each plan, pair a burial/cremation package in the cart with the matching recommended essentials bundle.
 */
const essentialsBundles = computed(() => {
	if (!priceBook.value) return []

	// Get essentials bundles by disposition type
	const bundlesByDisposition = Object.fromEntries(
		priceBook.value
			.getLineItems()
			.filter(
				(item) =>
					item.category === PbProductType.Package &&
					item.subcategory === PbSubcategory.PackageEssentials
			)
			.map((b) => [b.dispositionType, b])
	)

	return (session.activePlans ?? []).flatMap((plan) =>
		plan.cartItems.flatMap((ci) => {
			const li = priceBook.value?.findLineItem(ci.lineItemId)
			if (!li) return []

			if (
				li.subcategory === PbSubcategory.PackageBurial &&
				bundlesByDisposition.Burial
			) {
				return [{ bundle: bundlesByDisposition.Burial, plan, basePackage: li }]
			}

			if (
				li.subcategory === PbSubcategory.Cremation &&
				bundlesByDisposition.Cremation
			) {
				return [
					{ bundle: bundlesByDisposition.Cremation, plan, basePackage: li },
				]
			}

			return []
		})
	)
})

/**
 * Carriage packages flagged isNoCostEverplans already include Everplans,
 * so the recommended essentials bundle should not list it again.
 */
const bundleIncludesEverplans = (
	basePackage: PbLineItem,
	bundle: PbLineItem
) => {
	return !basePackage.isNoCostEverplans && !bundle.isNoCostEverplans
}

const isEverplansPackageProduct = (
	product: PbPackageProduct & { lineItem: PbLineItem | undefined }
) => {
	return (
		product.lineItem?.category === PbProductType.Everplans ||
		product.lineItem?.subcategory === PbSubcategory.Everplans
	)
}

/**
 * Returns sorted subitems for a package
 */
function getPackageSubItems(
	packageId: string,
	includesOapItem = false,
	includeEverplans = true
) {
	if (!priceBook.value) return []

	const withLineItem = priceBook.value.getProductsInPackage(packageId)
	const sorted = priceBook.value.getSortedPackageProducts(
		withLineItem,
		includesOapItem
	) as Array<
		PbPackageProduct & {
			lineItem: PbLineItem | undefined
			isIncludedInPackage?: boolean
		}
	>

	const packageProducts = includeEverplans
		? sorted
		: sorted.filter((product) => !isEverplansPackageProduct(product))

	const includedItems = packageProducts.filter(
		(p) => p.isIncludedInPackage !== false
	)
	const notIncludedItems = packageProducts.filter(
		(p) => p.isIncludedInPackage === false
	)

	if (!includeEverplans) {
		return [...includedItems, ...notIncludedItems]
	}

	// Everplans index should be 2 if the number of items is >= 3, 1 if 2, else append
	let insertIndex = includedItems.length
	if (includedItems.length >= 3) {
		insertIndex = 2
	} else if (includedItems.length === 2) {
		insertIndex = 1
	}

	includedItems.splice(insertIndex, 0, {
		lineItem: {
			id: 'EverplansLifetimeLineItem',
			name: 'Everplans Lifetime',
			isGuaranteed: true,
			category: PbProductType.Everplans,
			recordType: 'Product',
			pricingType: 'Fixed',
			fixedPrice: 299,
		},
		isIncludedInPackage: true,
	} as PbPackageProduct & {
		lineItem: PbLineItem | undefined
		isIncludedInPackage?: boolean
	})

	// Return included first (with Everplans inserted) and add not-included items after
	return [...includedItems, ...notIncludedItems]
}
</script>
